// PWA 狀態（GDO-21）：Service Worker 註冊與新版本、安裝提示、離線狀態。
// 只在正式建置註冊 Service Worker（開發伺服器不快取，避免看到舊的程式）。
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface Pwa {
  /** 有新版本等待啟用（使用者按「重新整理」才套用） */
  updateReady: boolean;
  applyUpdate: () => void;
  /** 瀏覽器提供安裝提示（桌面 Chrome／Edge、Android） */
  canInstall: boolean;
  install: () => Promise<void>;
  /** iOS Safari：沒有安裝提示，改為說明「加入主畫面」 */
  iosInstallHint: boolean;
  /** 已經以 App 模式開啟 */
  standalone: boolean;
  offline: boolean;
}

function useOnline(): boolean {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener('online', cb);
      window.addEventListener('offline', cb);
      return () => {
        window.removeEventListener('online', cb);
        window.removeEventListener('offline', cb);
      };
    },
    () => navigator.onLine,
    () => true,
  );
}

const standaloneQuery = '(display-mode: standalone)';

export function usePwa(): Pwa {
  // 使用者按了「重新整理」套用新版本（之後的 controllerchange 要重新整理頁面）
  const applied = useRef(false);
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(null);
  const [standalone, setStandalone] = useState(
    () =>
      window.matchMedia?.(standaloneQuery).matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true,
  );
  const online = useOnline();

  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
    let cancelled = false;
    const watch = (reg: ServiceWorkerRegistration) => {
      // 已經有控制這個頁面的舊版本時，新安裝好的版本才算「新版本」（第一次安裝不提示）
      if (reg.waiting && navigator.serviceWorker.controller) setWaiting(reg.waiting);
      reg.addEventListener('updatefound', () => {
        const next = reg.installing;
        next?.addEventListener('statechange', () => {
          if (next.state === 'installed' && navigator.serviceWorker.controller && !cancelled) setWaiting(next);
        });
      });
    };
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        watch(reg);
        // 回到前景時檢查有沒有新版本
        const onVisible = () => document.visibilityState === 'visible' && reg.update().catch(() => {});
        document.addEventListener('visibilitychange', onVisible);
      })
      .catch((e) => console.warn(JSON.stringify({ level: 'warn', msg: 'Service Worker 註冊失敗', error: String(e) })));
    // 新版本啟用後重新整理一次，讓頁面使用新版本的檔案。第一次安裝（原本沒有控制者）不重新整理：
    // 頁面已經是最新的，而且沒有必要打斷使用者（GDO-29）
    const hadController = !!navigator.serviceWorker.controller;
    let reloaded = false;
    const onController = () => {
      if (reloaded || (!hadController && !applied.current)) return;
      reloaded = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onController);
    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener('controllerchange', onController);
    };
  }, []);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setPrompt(e as InstallPromptEvent);
    };
    const onInstalled = () => {
      setPrompt(null);
      setStandalone(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const applyUpdate = useCallback(() => {
    applied.current = true;
    waiting?.postMessage('SKIP_WAITING');
  }, [waiting]);
  const install = useCallback(async () => {
    if (!prompt) return;
    await prompt.prompt();
    await prompt.userChoice.catch(() => null);
    setPrompt(null);
  }, [prompt]);

  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
  return {
    updateReady: !!waiting,
    applyUpdate,
    canInstall: !!prompt && !standalone,
    install,
    iosInstallHint: ios && !standalone && !prompt,
    standalone,
    offline: !online,
  };
}
